import React, { useCallback, useState, useEffect } from 'react'
import ReactGA from 'react-ga'
import { useDispatch, useSelector } from 'react-redux'
import { useFocusEffect } from '@react-navigation/native'
import { SafeAreaView, View, Platform } from 'react-native'
import { getPreferredLanes, deletePreferredLane } from '../../redux/preferredLane/preferredLanesActions'
import { preferredLaneDeleteSelector, preferredLaneSelector } from '../../redux/preferredLane/preferredLaneSelector'
import useStyles from './useStyles'
import Header from './components/Header'
import PreferredLanesList from './components/PreferredLanesList'
import { IconButton, AddLaneModal, ConfirmationDialog, Txt, Button } from '../../components'
import { useTheme } from '../../theming/ThemeProvider'
import { usePlatforms } from '../../hooks/usePlatforms'
import Map from '../../components/Map'
ReactGA.pageview('/preferred-lanes')

const PreferredLanes = () => {
  const dispatch = useDispatch()
  const { theme } = useTheme()
  const { isTablet } = usePlatforms()
  const styles = useStyles(theme, isTablet)
  const { preferredLanes } = useSelector(preferredLaneSelector)
  const [invalidLanes, setInvalidLanes] = useState([])

  const { deleteLoading } = useSelector(preferredLaneDeleteSelector)
  const [modalParams, setModalParams] = useState({
    openAddModal: false,
    openConfirmationModal: false,
    lane: null
  })

  useFocusEffect(
    useCallback(() => {
      dispatch(getPreferredLanes())
    }, [dispatch])
  )

  useEffect(() => {
    setInvalidLanes([])
  }, [preferredLanes])

  const editLane = useCallback((lane) => {
    setModalParams({
      openAddModal: true,
      openConfirmationModal: false,
      lane
    })
  }, [])

  const deleteLane = useCallback((lane) => {
    setModalParams({
      openAddModal: false,
      openConfirmationModal: true,
      lane
    })
  }, [])

  const onCloseModal = useCallback(() => {
    setModalParams({
      openAddModal: false,
      openConfirmationModal: false,
      lane: null
    })
  }, [])

  const handleConfirmationDialog = useCallback((action) => {
    if (action) {
      dispatch(deletePreferredLane(modalParams.lane.lane_id, onCloseModal))
    } else {
      onCloseModal()
    }
  }, [dispatch, modalParams, onCloseModal])

  return (
    <>
      <View style={styles.container} testID='preferredLaneScreen' accessibilityLabel='preferredLaneScreen'>
        <SafeAreaView />
        <View style={styles.header}>
          <Header />
        </View>
        <View style={styles.content}>
          <View style={styles.list}>
            <View style={styles.texts}>
              {isTablet && (
                <Txt
                  text='Preferred Lanes'
                  identity='h3'
                />
              )}
              <Txt
                text='Data on available loads will be sorted based on the lanes and requirements listed below.'
                identity='subtitle'
                color='SLATE_GRAY'
              />
              {isTablet && (
                <View style={styles.addButtonLargeContainer}>
                  <Button
                    title='Add Lane'
                    onPress={editLane}
                    style={styles.addButtonLarge}
                  />
                </View>
              )}
            </View>
            <PreferredLanesList
              invalidLanes={invalidLanes}
              editLane={editLane}
              deleteLane={deleteLane}
            />
          </View>
          {isTablet && Platform.OS === 'web' && (
            <View style={styles.mapContainer}>
              {Boolean(preferredLanes.length) && (
                <View style={styles.map}>
                  <Map
                    routes={preferredLanes.map(lane => [
                      [lane.origin.latitude, lane.origin.longitude],
                      [lane.destination.latitude, lane.destination.longitude]
                    ])}
                    handleInvalidRoute={(newIndex) => setInvalidLanes(state => [...state, newIndex])}
                    mapId='lanesMap'
                  />
                </View>
              )}
            </View>
          )}
        </View>
        {!isTablet && (
          <View style={styles.addButton}>
            <IconButton
              color='AZTEC'
              icon='Plus'
              size={40}
              iconSize={20}
              onPress={() => editLane()}
              testID='preferredLanesAddButton'
              accessibilityLabel='preferredLanesAddButton'
            />
          </View>
        )}
        <AddLaneModal
          open={modalParams.openAddModal}
          onClose={onCloseModal}
          laneToEdit={modalParams.lane}
        />
        <ConfirmationDialog
          open={modalParams.openConfirmationModal}
          title='Delete Lane'
          description='Are sure you want to delete this lane?'
          onClose={handleConfirmationDialog}
          loading={deleteLoading}
        />
      </View>
    </>
  )
}

export default PreferredLanes
